import { useEffect, useState } from 'react';
import authAxios from '../auth/authAxios';
import { CheckCircle2, Lock, Trophy, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

interface Challenge {
  challenge_id: string;
  title: string;
  description: string;
  completed: boolean;      // Overall challenge completed flag
  locked: boolean;         // Challenge locked flag, computed from user progress maybe
  progress: number;        // 0-100 percentage of lessons completed
}

const ChallengePage = () => {
  const [challenges, setChallenges] = useState<Challenge[]>([]);

  useEffect(() => {
    authAxios.get('/api/challenges/summary') // You can create a lighter API for just summary info
      .then(res => setChallenges(res.data))
      .catch(err => console.error('Failed to load challenges', err));
  }, []);

  if (!challenges.length) return <p className="p-6">Loading challenges...</p>;

  return (
    <main className="max-w-[80vw] p-6">
      <h2 className="text-3xl font-futuristic font-semibold mb-8 tracking-wide">Challenges</h2>
      <div className="rounded-xl bg-gradient-to-br from-cyan-500/20 via-primary/10 to-transparent border border-primary/30 p-6 flex items-center justify-between gap-4 shadow-lg hover:shadow-xl transition mb-5">
        <div className="flex items-center gap-4">
          <img
              src="/ai-challenges.png"
              alt="Get Started!"
              className="w-16 h-16 object-contain drop-shadow"
            />
          <div>
            <h4 className="text-xl font-semibold text-white mb-1">{challenges.filter(l => !l.completed).length > 0 ? "Select a Challenge" : "You're all Caught Up!"}</h4>
            <p className="text-white/80 text-sm">
            {challenges.filter(l => !l.completed).length > 0 ? "Click any Available Challenge to begin.  You can also select a Completed Challenge to try for a higher score." : "Congratulations!  You can click on any Completed Challenge to try for a higher score.  Check back soon for more Challenges."}
            </p>
          </div>
        </div>
        <div>&nbsp;</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {challenges.map(challenge => (
          <Link
            to={`/challenges/${challenge.challenge_id}`}
            key={challenge.challenge_id}
            className={`p-6 rounded-xl bg-bg-panel bg-opacity-90 backdrop-blur-sm border border-white/10 shadow-[0_0_10px_rgba(79,255,222,0.1)] flex items-center justify-between hover:bg-cyan-500/20
              ${challenge.locked ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-lg hover:scale-[1.05] transition-transform duration-200 cursor-pointer'}
            `}
          >
            <div>
              <h3 className="text-xl font-semibold text-white flex items-center gap-2">
                <Sparkles className="text-primary w-5 h-5" />
                {challenge.title}
              </h3>
              <p className="text-white/80 text-sm mt-1">{challenge.description}</p>
              <>
                {challenge.progress == 100 && (<div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                  <span className="text-sm text-green-400">Completed</span>
                </div>)}
    
                {challenge.progress < 100 && (<div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-4 h-4 text-yellow-400" />
                  <span className="text-sm text-yellow-400">{Math.round(challenge.progress)}% complete</span>
                </div>)}
              </>
            </div>
            <div className="flex items-center gap-3">
              {challenge.completed && <Trophy className="text-yellow-400 w-6 h-6" />}
              {challenge.locked && <Lock className="text-white/40 w-5 h-5" />}
            </div>
          </Link>
        ))}
      </div>
    </main>
  );
};

export default ChallengePage;
